import React, { Suspense, lazy, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { usePublicSettings } from './hooks/useApi';
import api from './lib/api';
import Layout from './components/layout/Layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { Toaster } from 'sonner';
import { useTheme } from './hooks/useTheme';

function ThemedToaster() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  return (
    <Toaster position="top-center" theme={theme} richColors closeButton duration={5000}
      toastOptions={{ style: { background: dark ? 'rgba(12,16,28,.96)' : 'rgba(255,255,255,.97)', border: dark ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(11,16,33,.1)', color: dark ? '#fff' : '#0B1021', fontFamily: 'Manrope, sans-serif', fontSize: '13px', borderRadius: '14px', backdropFilter: 'blur(16px)' } }} />
  );
}

// Eager: core pages
import Home from './pages/Home';

// Lazy-loaded pages (code splitting)
const Marketplace = lazy(() => import('./pages/Marketplace'));
const MarketplaceShow = lazy(() => import('./pages/MarketplaceShow'));
const Resources = lazy(() => import('./pages/Resources'));
const ResourceShow = lazy(() => import('./pages/ResourceShow'));
const Forums = lazy(() => import('./pages/Forums'));
const ThreadShow = lazy(() => import('./pages/ThreadShow'));
const Rankings = lazy(() => import('./pages/Rankings'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogShow = lazy(() => import('./pages/BlogShow'));
const Wallet = lazy(() => import('./pages/Wallet'));
const Plus = lazy(() => import('./pages/Plus'));
const NuyCoin = lazy(() => import('./pages/NuyCoin'));
const Feed = lazy(() => import('./pages/Feed'));
const Streaming = lazy(() => import('./pages/Streaming'));
const Chat = lazy(() => import('./pages/Chat'));
const Messages = lazy(() => import('./pages/Messages'));
const Profile = lazy(() => import('./pages/Profile'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const Cart = lazy(() => import('./pages/Cart'));
const Admin = lazy(() => import('./pages/Admin'));
const Legal = lazy(() => import('./pages/Legal'));
const CreateForm = lazy(() => import('./pages/CreateForm'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Members = lazy(() => import('./pages/Members'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const MyPages = lazy(() => import('./pages/MyPages'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentResult').then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancelPage = lazy(() => import('./pages/PaymentResult').then((m) => ({ default: m.PaymentCancel })));

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="gx-loader"><span /><span /><span /></div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const SEO_MAP = [
  ['/marketplace', 'Marketplace — NUYED', 'P2P marketplace with escrow protection. Accounts, digital goods and services traded in USD or NUY.'],
  ['/resources', 'Resources Hub — NUYED', 'Download themes, plugins, scripts and tools. Free & premium resources with reviews.'],
  ['/forums', 'Forums — NUYED', 'The NUYED community forums. Discuss, share and earn NUY for quality posts.'],
  ['/blog', 'Blog — NUYED', 'News, releases, guides and deep-dives from the NUYED community.'],
  ['/feed', 'Live Feed — NUYED', 'Real-time community feed of NUYED members.'],
  ['/rankings', 'Rankings — NUYED', 'Top members by NUY and activity.'],
  ['/wallet', 'NUY Wallet — NUYED', 'Buy, spend and withdraw NUY — the native NUYED coin.'],
  ['/streaming', 'Streaming — NUYED', 'Watch the community live on Twitch, Kick and YouTube.'],
  ['/messages', 'Messages — NUYED', 'Private direct messages.'],
  ['/affiliate', 'Affiliate Program — NUYED', 'Invite friends and earn 15% of everything they spend.'],
  ['/plus', 'NUYED Plus', 'Unlock premium perks with NUYED Plus.'],
  ['/nuycoin', 'Nuy Coin — NUYED', 'Everything about NUY, the NUYED economy.'],
];

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function SeoManager() {
  const { pathname } = useLocation();
  const { data: site } = usePublicSettings();
  useEffect(() => {
    const hit = SEO_MAP.find(([p]) => pathname === p || pathname.startsWith(p + '/'));
    const title = hit ? hit[1] : (site?.seo_title || 'NUYED — The modern community, marketplace & NUY economy');
    const desc = hit ? hit[2] : (site?.seo_description || 'Forums, P2P marketplace with escrow, downloadable resources, live streams and the NUY economy — all in one place.');
    document.title = title;
    setMeta('name', 'description', desc);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', window.location.href);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', desc);
    if (site?.favicon_url) {
      let l = document.querySelector('link[rel="icon"]');
      if (!l) { l = document.createElement('link'); l.rel = 'icon'; document.head.appendChild(l); }
      l.href = site.favicon_url;
    }
  }, [pathname, site]);
  useEffect(() => {
    const beat = () => { if (localStorage.getItem('nuyed_token')) api.post('/presence/heartbeat').catch(() => {}); };
    beat();
    const iv = setInterval(beat, 60000);
    return () => clearInterval(iv);
  }, []);
  return null;
}

const MyPagesModule = () => import('./pages/MyPages');
const MyResourcesPage = lazy(() => MyPagesModule().then((m) => ({ default: m.MyResources })));
const MyOrdersPage = lazy(() => MyPagesModule().then((m) => ({ default: m.MyOrders })));
const MySalesPage = lazy(() => MyPagesModule().then((m) => ({ default: m.MySales })));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const FeedPost = lazy(() => import('./pages/FeedPost'));

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <ThemedToaster />
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SeoManager />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/feed/post/:id" element={<FeedPost />} />
                <Route path="/users" element={<Members />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />

                <Route path="/forums" element={<Forums />} />
                <Route path="/forums/new" element={<CreateForm kind="thread" />} />
                <Route path="/forums/thread/:slug" element={<ThreadShow />} />
                <Route path="/forums/:category" element={<Forums />} />

                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/new" element={<CreateForm kind="listing" />} />
                <Route path="/marketplace/orders" element={<MyOrdersPage />} />
                <Route path="/marketplace/orders/sell" element={<MySalesPage />} />
                <Route path="/marketplace/orders/:id" element={<OrderDetail />} />
                <Route path="/marketplace/:slug" element={<MarketplaceShow />} />

                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/new" element={<CreateForm kind="resource" />} />
                <Route path="/resources/mine" element={<MyResourcesPage />} />
                <Route path="/resources/:slug" element={<ResourceShow />} />

                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/new" element={<CreateForm kind="blog" />} />
                <Route path="/blog/:slug" element={<BlogShow />} />

                <Route path="/rankings" element={<Rankings />} />
                <Route path="/plus" element={<Plus />} />
                <Route path="/nuy-coin" element={<NuyCoin />} />
                <Route path="/streaming" element={<Streaming />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/messages" element={<Messages />} />

                <Route path="/wallet" element={<Wallet />} />
                <Route path="/wallet/buy" element={<Wallet />} />
                <Route path="/wallet/withdraw" element={<Wallet />} />
                <Route path="/wallet/transactions" element={<Wallet />} />

                <Route path="/cart" element={<Cart />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="/affiliate" element={<Affiliate />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/user/:username" element={<Profile />} />

                <Route path="/admin" element={<Admin />} />

                <Route path="/terms" element={<Legal page="terms" />} />
                <Route path="/privacy" element={<Legal page="privacy" />} />
                <Route path="/dmca" element={<Legal page="dmca" />} />
                <Route path="/cookies" element={<Legal page="cookies" />} />
                <Route path="/refunds" element={<Legal page="refunds" />} />
                <Route path="/legal/:page" element={<Legal />} />

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
